/**
 * Seção "Prazos por rota": quantos dias úteis a unidade promete por rota e
 * a folga do setor. Vale para as PRÓXIMAS autorizações — o prazo de quem já
 * foi autorizado é um retrato e não muda (só por replanejamento).
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiRotateCcw } from "react-icons/fi";

import AsyncButton from "../../../../components/AsyncButton";
import { useAuth } from "../../../../context/AuthContext";
import {
  calcularPrazosRetroativos, contarPrazosRetroativos, obterRegrasPrazo, salvarRegrasPrazo,
} from "../../../../services/solicitacoes";
import { TableWrapper } from "../../../../styles/shared";
import { Carregando } from "../../_shared/Estados";
import {
  Checkbox, ErrorMsg, GhostBtn, InlineError, Input, NeutralBanner, RowEnd, Table, Td, Th,
} from "../styles";
import { regrasCompletas, validarRegrasPrazo } from "./regras";

/**
 * Ação única (decisão de 22/09/2026): quem foi autorizado antes de a regra de
 * prazo existir ganha prazo contado da data da autorização. Idempotente —
 * some quando não há mais nenhuma pendente.
 */
function PrazoRetroativo() {
  const { request } = useAuth();
  const [pendentes, setPendentes] = useState(null);
  const [confirmando, setConfirmando] = useState(false);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    contarPrazosRetroativos(request)
      .then((r) => setPendentes(r?.pendentes ?? 0))
      // Sem a contagem o bloco simplesmente não aparece.
      .catch(() => setPendentes(0));
  }, [request]);

  if (!pendentes && !resultado) return null;

  const executar = async () => {
    const r = await calcularPrazosRetroativos(request);
    setResultado(r);
    setPendentes(r?.pendentes ?? 0);
    setConfirmando(false);
  };

  return (
    <NeutralBanner style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {resultado ? (
        <span>
          {resultado.calculados} solicitação(ões) ganharam prazo pela data da autorização.
          {(resultado.sem_regra || []).length > 0 && ` ${resultado.sem_regra.length} ficaram sem prazo porque a rota delas não tem regra ativa.`}
        </span>
      ) : (
        <>
          <span>
            <strong>{pendentes}</strong> solicitação(ões) foram autorizadas antes de o prazo existir e estão
            "Sem prazo". Calcular dá a cada uma o prazo da regra atual, contado da data em que foi autorizada.
            As já entregues passam a contar na aderência do painel.
          </span>
          <RowEnd>
            {confirmando ? (
              <>
                <GhostBtn type="button" onClick={() => setConfirmando(false)}>Voltar</GhostBtn>
                <AsyncButton variant="primary" silentError={false} successMessage="Prazos calculados." onClick={executar}>
                  Confirmar cálculo de {pendentes}
                </AsyncButton>
              </>
            ) : (
              <GhostBtn type="button" onClick={() => setConfirmando(true)}>Calcular prazo das autorizadas sem prazo</GhostBtn>
            )}
          </RowEnd>
        </>
      )}
    </NeutralBanner>
  );
}

export default function PrazosSecao({ podeEditar }) {
  const { request } = useAuth();
  const [payload, setPayload] = useState(null);
  const [regras, setRegras] = useState([]);
  const [erro, setErro] = useState("");
  const [erroEnvio, setErroEnvio] = useState("");
  const [sujo, setSujo] = useState(false);

  const aplicar = useCallback((p) => {
    setPayload(p);
    setRegras(regrasCompletas(p));
    setSujo(false);
  }, []);

  useEffect(() => {
    obterRegrasPrazo(request).then(aplicar)
      .catch((e) => setErro(e?.message || "Erro ao carregar as regras de prazo."));
  }, [request, aplicar]);

  const mudar = (rota, campo, valor) => {
    setErroEnvio("");
    setSujo(true);
    setRegras((prev) => prev.map((r) => (r.rota === rota ? { ...r, [campo]: valor } : r)));
  };

  const restaurarPadrao = () => {
    setErroEnvio("");
    setSujo(true);
    setRegras(regrasCompletas({ ...payload, regras: [] }));
  };

  const salvar = async () => {
    const msg = validarRegrasPrazo(regras);
    if (msg) { setErroEnvio(msg); throw new Error(msg); }
    try {
      aplicar(await salvarRegrasPrazo(request, regras.map((r) => ({
        rota: r.rota,
        dias_uteis_entrega: Number(r.dias_uteis_entrega),
        folga_setor_dias_uteis: Number(r.folga_setor_dias_uteis),
        ativo: r.ativo,
      }))));
    } catch (e) {
      setErroEnvio(e?.message || "Não foi possível salvar.");
      throw e;
    }
  };

  if (erro) return <ErrorMsg>{erro}</ErrorMsg>;
  if (!payload) return <Carregando />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <NeutralBanner>
        O prazo nasce na autorização: a rota vem das peças (a de maior prazo do documento) e os dias úteis
        seguem o calendário da unidade (Configurações &gt; Calendário). Mudar aqui vale para as próximas autorizações.
      </NeutralBanner>
      <TableWrapper>
        <Table>
          <colgroup>
            <col />
            <col style={{ width: 170 }} />
            <col style={{ width: 170 }} />
            <col style={{ width: 90 }} />
          </colgroup>
          <thead>
            <tr>
              <Th>Rota</Th>
              <Th title="Dias úteis da autorização até a entrega prometida">Entrega (d.u.)</Th>
              <Th title="O setor termina N dias úteis antes da entrega">Folga do setor (d.u.)</Th>
              <Th>Ativa</Th>
            </tr>
          </thead>
          <tbody>
            {regras.map((r) => (
              <tr key={r.rota}>
                <Td title={r.rota_display}>{r.rota_display}</Td>
                <Td>
                  <Input type="number" min="1" value={r.dias_uteis_entrega} disabled={!podeEditar}
                         style={{ height: 32, maxWidth: 110 }}
                         onChange={(e) => mudar(r.rota, "dias_uteis_entrega", e.target.value)} />
                </Td>
                <Td>
                  <Input type="number" min="0" value={r.folga_setor_dias_uteis} disabled={!podeEditar}
                         style={{ height: 32, maxWidth: 110 }}
                         onChange={(e) => mudar(r.rota, "folga_setor_dias_uteis", e.target.value)} />
                </Td>
                <Td>
                  <Checkbox checked={!!r.ativo} disabled={!podeEditar}
                            onChange={(e) => mudar(r.rota, "ativo", e.target.checked)} />
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
      {erroEnvio && <InlineError style={{ fontSize: 12.5 }}>{erroEnvio}</InlineError>}
      {podeEditar && (
        <RowEnd>
          <GhostBtn type="button" onClick={restaurarPadrao} title="Corte 3 · usinagem/pintura 6 · terceiro 9 · mão de obra 3 · almoxarifado 3">
            <FiRotateCcw size={14} /> Restaurar padrão
          </GhostBtn>
          <AsyncButton variant="primary" silentError successMessage="Regras de prazo salvas."
                       onClick={salvar} disabled={!sujo}>
            Salvar prazos
          </AsyncButton>
        </RowEnd>
      )}
      {podeEditar && <PrazoRetroativo />}
    </div>
  );
}
